import { useTranslation } from 'react-i18next';
import { getSpeakerOrderMap } from '../utils/getThreadSpeakerColor';
import type { components } from '@pocapan/shared/types/schema';

type CastRelationship = components['schemas']['ApiV1CastRelationship'];
type SceneContent =
  | components['schemas']['ApiV1SceneChat']
  | components['schemas']['ApiV1SceneThread'];
type ContentWithSpeaker = { speaker?: string | null; sequence?: number };

interface StatusBarDetail {
  likesCount: number;
  commentsCount: number;
  isLiked: boolean;
}

interface TagsSource {
  keywords?: string[];
  worldview?: string[];
  totalCount?: number;
  sequenceNo?: number;
}

interface PreviewDetailLike extends TagsSource {
  title?: string | null;
  previewType?: string | null;
  ageRating?: string | null;
  likesCount?: number;
  commentsCount?: number;
  isLiked?: boolean;
  fictionTitle?: string | null
  adaptedFictionEditionId?: string | null;
}

interface SceneDetailLike extends TagsSource {
  title?: string | null;
  contentType?: string | null;
  castRelationship?: CastRelationship | null;
  likesCount?: number;
  commentsCount?: number;
  isLiked?: boolean;
  adaptedFictionEditionId?: string | null;
}

interface UseSceneDetailDisplayProps {
  previewDetail: PreviewDetailLike | null | undefined;
  sceneDetail: SceneDetailLike | null | undefined;
  contents: SceneContent[];
  introContents: ContentWithSpeaker[];
  isPreviewMode: boolean;
  isLoading: boolean;
  isBeforeMode: boolean;
}

export const useSceneDetailDisplay = ({
  previewDetail,
  sceneDetail,
  contents,
  introContents,
  isPreviewMode,
  isLoading,
  isBeforeMode
}: UseSceneDetailDisplayProps) => {
  const { t } = useTranslation();

  const showIntroContents =
    isPreviewMode && !!previewDetail && (isLoading || contents.length === 0) && !isBeforeMode;
  const displayContents =
    isPreviewMode && introContents.length > 0 ? [...introContents, ...contents] : contents;

  const hasSequenceOneInList = showIntroContents
    ? true
    : contents.some(
        (content) =>
          ('sequenceGroupNo' in content
            ? content.sequenceGroupNo
            : (content as { sequence?: number }).sequence) === 1
      );

  const speakerOrderMap =
    showIntroContents || (isPreviewMode && contents.length > 0)
      ? getSpeakerOrderMap(displayContents)
      : getSpeakerOrderMap(contents);
  const lastSpeaker =
    showIntroContents || (isPreviewMode && contents.length > 0)
      ? ([...displayContents].reverse().find((c) => c.speaker)?.speaker ?? null)
      : ([...contents].reverse().find((c) => c.speaker)?.speaker ?? null);

  const detailForHeader = sceneDetail?.castRelationship ?? null;
  const displayTitle =
    (isPreviewMode ? previewDetail?.fictionTitle : sceneDetail?.title) ??
    (t('ScenePreviewDetail.pageTitle') as string);
  const displayContentType =
    (isPreviewMode ? previewDetail?.previewType : sceneDetail?.contentType) ?? undefined;
  const displayTagsSource = (isPreviewMode ? previewDetail : sceneDetail) ?? null;
  const displayIsR19 = isPreviewMode && previewDetail?.ageRating === 'R-19';

  const statusBarDetail: StatusBarDetail | null =
    isPreviewMode && previewDetail
      ? {
          likesCount: previewDetail.likesCount ?? 0,
          commentsCount: previewDetail.commentsCount ?? 0,
          isLiked: previewDetail.isLiked ?? false
        }
      : !isPreviewMode && sceneDetail
        ? {
            likesCount: sceneDetail.likesCount ?? 0,
            commentsCount: sceneDetail.commentsCount ?? 0,
            isLiked: sceneDetail.isLiked ?? false
          }
        : null;

  const effectiveAdaptedFictionEditionId = isPreviewMode
    ? (previewDetail?.adaptedFictionEditionId ?? null)
    : (sceneDetail?.adaptedFictionEditionId ?? null);

  return {
    display: {
      title: displayTitle,
      contentType: displayContentType,
      tagsSource: displayTagsSource,
      isR19: displayIsR19,
      statusBarDetail,
      effectiveAdaptedFictionEditionId,
      detailForHeader,
      speakerOrderMap,
      lastSpeaker,
      showIntroContents,
      hasSequenceOneInList
    }
  };
};
